<template>
  <div class="text-left" data-testid="upload-self-check-step">
    <p class="text-md font-bold text-textPrimary mb-1">Antes de enviar, confira a folha</p>

    <p v-if="isLoading" class="text-sm text-neutral50 mb-3" data-testid="upload-self-check-loading">
      Consultando a escala prevista...
    </p>
    <p
      v-else-if="expectedSummaryText"
      class="text-sm text-neutral50 mb-3"
      data-testid="upload-self-check-expected"
    >
      {{ expectedSummaryText }}
    </p>
    <!--
      Falha (ou ausência) do `expected-summary` não impede o checklist: a declaração do emissor
      vale por si, e travar o envio numa consulta auxiliar seria pior que enviar sem o resumo.
    -->
    <p v-else class="text-sm text-neutral50 mb-3" data-testid="upload-self-check-expected-missing">
      Páginas enviadas: {{ pages }}.
    </p>
    <!-- M13-B: ocupados sem identificação ficam fora do universo esperado (Q22) — só avisa. -->
    <p
      v-if="!isLoading && unidentifiedNotice"
      class="text-sm text-[#68688D] -mt-2 mb-3"
      data-testid="upload-self-check-unidentified"
    >
      {{ unidentifiedNotice }}
    </p>

    <div class="flex flex-col gap-1 border border-gray-200 rounded-lg px-4 py-3">
      <label
        v-for="item in items"
        :key="item.key"
        class="flex items-start gap-2 text-sm text-textPrimary cursor-pointer"
        :data-testid="`upload-self-check-item-${item.key}`"
      >
        <input
          type="checkbox"
          class="mt-0.5"
          :checked="state[item.key]"
          :data-testid="`upload-self-check-checkbox-${item.key}`"
          @change="toggle(item.key)"
        />
        <span>{{ item.label }}</span>
      </label>
    </div>

    <p class="text-xs text-neutral50 mt-3">
      "Enviar assim mesmo" grava a sua declaração no histórico do registro; o auditor vê o que foi
      apontado antes de abrir a folha.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Network from '@/Network'
import type { DSAttendanceExpectedSummaryItem } from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import {
  buildExpectedSummaryText,
  buildSelfCheckItems,
  buildUnidentifiedCoverageNotice,
  totalExpectedDoctorStations,
  type SelfCheckKey,
  type SelfCheckState
} from './self-check'

/**
 * Eixo 2A — passo do checklist no envio da folha. Não valida nada: nenhum item marcado ainda
 * permite "Enviar assim mesmo". O que o passo entrega é a declaração do emissor e o número de
 * plantões que a escala prevê, que é a informação que ele não tinha ao olhar a folha.
 */

const props = defineProps<{
  /** Estado dos 4 itens, dono no modal (o botão de enviar lê daqui). */
  modelValue: SelfCheckState
  /** Data do registro, já formatada em dd/MM/yyyy. */
  formattedDate: string
  healthUnitId: number | null
  scheduleIds: number[]
  /** Data de referência em ISO (yyyy-MM-dd) — o que a rota espera. */
  referenceDate: string
  /** Páginas/imagens anexadas; vira `expectedPages` no envio. */
  pages: number
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: SelfCheckState): void
}>()

const state = computed(() => props.modelValue)

const summary = ref<DSAttendanceExpectedSummaryItem[] | null>(null)
const isLoading = ref(false)

const expectedSummaryText = computed(() =>
  buildExpectedSummaryText({ summary: summary.value, pages: props.pages })
)

const unidentifiedNotice = computed(() => buildUnidentifiedCoverageNotice(summary.value))

const items = computed(() =>
  buildSelfCheckItems({
    formattedDate: props.formattedDate,
    expectedDoctorStations: totalExpectedDoctorStations(summary.value)
  })
)

function toggle(key: SelfCheckKey) {
  emit('update:modelValue', { ...props.modelValue, [key]: !props.modelValue[key] })
}

async function loadExpectedSummary() {
  if (!props.healthUnitId || !props.referenceDate || props.scheduleIds.length === 0) return

  isLoading.value = true
  try {
    summary.value = await Network.dsAttendance.getExpectedSummary({
      healthUnitId: props.healthUnitId,
      referenceDate: props.referenceDate,
      scheduleIds: props.scheduleIds
    })
  } catch (error) {
    // Silencioso de propósito: o checklist é auxiliar do envio, e um toast de erro aqui
    // sugeriria ao emissor que o envio falhou.
    console.debug('[AttendanceUpload] escala prevista indisponível:', error)
    summary.value = null
  } finally {
    isLoading.value = false
  }
}

watch(
  () => [props.healthUnitId, props.referenceDate, props.scheduleIds.join(',')],
  () => loadExpectedSummary(),
  { immediate: true }
)
</script>
